<template lang="pug">
.row-menu
  //- Acción única: el botón, sin menú de por medio.
  template(v-if="!plegado()")
    slot
    RowAction(
      v-for="(item, i) in accionesItems"
      :key="i"
      :icon="item.icon"
      :tooltip="item.label"
      :danger="isDanger(item)"
      :disabled="item.disabled"
      @click="run(item)"
    )

  template(v-else)
    button.row-menu-trigger(
      ref="trigger"
      type="button"
      :aria-label="label"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click.stop="toggle"
    )
      i.pi.pi-chevron-down

    teleport(to="body")
      transition(name="row-menu")
        ul.row-menu-panel(
          v-if="open"
          ref="panel"
          role="menu"
          :style="panelStyle"
          @click.stop
        )
          //- Modo declarativo: los RowAction del slot se pintan como items.
          slot
          //- Modo array: mismo formato que usaba el SplitButton.
          template(v-for="(item, i) in visibleItems" :key="i")
            li.row-menu-separator(v-if="item.separator" role="separator")
            li(v-else role="none")
              button.row-menu-item(
                type="button"
                role="menuitem"
                :disabled="item.disabled"
                :class="{ 'is-danger': isDanger(item) }"
                @click="run(item)"
              )
                i.row-menu-icon(v-if="item.icon" :class="item.icon")
                span {{ item.label }}
</template>

<script>
/**
 * Menú de acciones de una fila de tabla.
 *
 * Está hecho a mano y no sobre el Menu de PrimeVue por un motivo concreto:
 * en una tabla hay una instancia por fila, y el Menu en modo popup registra
 * un listener global de "click afuera" por instancia. El click que abre uno
 * es "afuera" para los otros nueve, así que se cerraban entre sí y el menú
 * quedaba a medio abrir, con los items inertes.
 *
 * Acá el listener global existe solo mientras hay un menú abierto, y el
 * panel viaja al body con <teleport> para que no lo recorte el overflow de
 * la tabla, calculando su posición contra el disparador.
 *
 * Admite las dos formas de declarar las acciones:
 *
 *   RowMenu(:items="getActionMenu(slotProps.data)")   // array
 *
 *   RowMenu                                            // declarativo
 *     RowAction(icon="pi pi-pencil" tooltip="Editar" @click="...")
 *
 * En el array, cada item es { label, icon, command, disabled?, separator? }.
 * Una acción se pinta como destructiva si trae `danger: true` o si su ícono
 * es un tacho.
 */
import { Comment, Fragment } from 'vue';

const MIN_WIDTH = 208;
const GAP = 4;
const MARGIN = 8;

/**
 * Cuántas acciones trae el slot.
 *
 * Un `v-if` que no se cumple deja un vnode de comentario, y un `template`
 * deja un fragmento con los hijos adentro: hay que mirar dentro de los
 * fragmentos para no contar como acción algo que no se renderiza.
 */
function contarNodos(nodos) {
  if (!nodos) return 0;

  return nodos.reduce((total, nodo) => {
    if (nodo.type === Comment) return total;
    if (nodo.type === Fragment) return total + contarNodos(nodo.children);
    return total + 1;
  }, 0);
}

export default {
  name: 'RowMenu',
  provide() {
    // RowAction lo inyecta para saber cómo dibujarse. `plegado` es un getter
    // y no un valor: se resuelve durante el render del hijo, así que sigue
    // al padre cuando cambia la cantidad de acciones de la fila.
    const menu = this;

    return {
      rowMenu: {
        close: () => menu.close(),
        get plegado() {
          return menu.plegado();
        },
      },
    };
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: 'Acciones',
    },
  },
  data() {
    return {
      open: false,
      panelStyle: {},
    };
  },
  computed: {
    // `visible: false` esconde una acción que no aplica a esa fila; es el
    // mismo contrato que traían los modelos del SplitButton.
    visibleItems() {
      return this.items.filter(item => item.visible !== false);
    },

    /** Los items que son acciones: sin los separadores, que no se ejecutan. */
    accionesItems() {
      return this.visibleItems.filter(item => !item.separator);
    },
  },
  beforeUnmount() {
    this.unbind();
  },
  methods: {
    /**
     * Con una sola acción no hay nada que plegar: se muestra el botón y
     * listo, porque desplegar un menú para elegir entre una opción es un
     * clic de más. Con ninguna no se muestra nada —un rol del sistema, el
     * saldo inicial de una cuenta—, en vez de abrir un panel vacío.
     *
     * Es un método y no un computed a propósito: cuenta el contenido del
     * slot, y $slots no se registra como dependencia reactiva, así que un
     * computed se quedaría con el primer valor aunque un v-if de la fila
     * cambiara sus acciones. Los métodos se evalúan en cada render.
     */
    plegado() {
      return this.accionesItems.length + contarNodos(this.$slots.default?.()) > 1;
    },

    isDanger(item) {
      return item.danger === true || (item.icon || '').includes('pi-trash');
    },

    toggle() {
      this.open ? this.close() : this.show();
    },

    async show() {
      // Se monta oculto para poder medirlo: en modo declarativo la cantidad
      // de acciones la decide el slot (con sus v-if), así que el alto real
      // no se puede estimar de antemano.
      this.panelStyle = { visibility: 'hidden', minWidth: `${MIN_WIDTH}px` };
      this.open = true;

      // En el próximo frame: si se enganchara ahora, este mismo click
      // llegaría al listener y cerraría el menú recién abierto.
      requestAnimationFrame(() => {
        document.addEventListener('click', this.onOutside);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.close);
        window.addEventListener('scroll', this.close, true);
      });

      await this.$nextTick();
      this.position();
    },

    close() {
      if (!this.open) return;
      this.open = false;
      this.unbind();
    },

    unbind() {
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.close);
      window.removeEventListener('scroll', this.close, true);
    },

    onOutside() {
      this.close();
    },

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        this.$refs.trigger?.focus();
      }
    },

    /** Ancla el panel al disparador, plegándolo si no entra en pantalla. */
    position() {
      const panel = this.$refs.panel;
      if (!panel) return;

      const r = this.$refs.trigger.getBoundingClientRect();
      const { height: alto, width: ancho } = panel.getBoundingClientRect();

      // Alineado por el borde derecho del disparador; si se saliera por la
      // izquierda del viewport, se corre lo mínimo para entrar.
      const left = Math.max(MARGIN, r.right - ancho);

      // Abajo por defecto; arriba si no hay lugar.
      const cabeAbajo = r.bottom + GAP + alto <= window.innerHeight - MARGIN;
      const top = cabeAbajo ? r.bottom + GAP : Math.max(MARGIN, r.top - GAP - alto);

      this.panelStyle = {
        top: `${top}px`,
        left: `${left}px`,
        minWidth: `${MIN_WIDTH}px`,
      };
    },

    run(item) {
      this.close();
      if (typeof item.command === 'function') {
        item.command({ item });
      }
    },
  },
};
</script>

<style scoped>
.row-menu {
  display: inline-flex;
}

.row-menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: var(--escality-radius-full);
  background: transparent;
  color: var(--escality-gray-500);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.row-menu-trigger:hover {
  color: var(--escality-primary);
  background: var(--escality-primary-50);
}

.row-menu-trigger:focus-visible {
  outline: 2px solid var(--escality-primary);
  outline-offset: 2px;
}
</style>

<style>
/* Sin scope: el panel vive en el body por el teleport. */
.row-menu-panel {
  position: fixed;
  z-index: 1100;
  margin: 0;
  padding: 0.375rem;
  list-style: none;
  background: var(--escality-surface);
  border: 1px solid var(--escality-gray-200);
  border-radius: var(--escality-radius-md);
  box-shadow: var(--escality-shadow-lg);
}

.row-menu-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  border: none;
  border-radius: var(--escality-radius-sm);
  background: transparent;
  color: var(--escality-gray-700);
  font-family: inherit;
  font-size: var(--escality-text-base);
  text-align: left;
  cursor: pointer;
}

.row-menu-item:hover:not(:disabled) {
  background: var(--escality-primary-50);
  color: var(--escality-primary);
}

.row-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.row-menu-item.is-danger:hover:not(:disabled) {
  background: var(--escality-danger-50);
  color: var(--escality-danger-dark);
}

.row-menu-icon {
  width: 1rem;
  font-size: var(--escality-text-base);
  color: var(--escality-gray-400);
}

.row-menu-item:hover:not(:disabled) .row-menu-icon {
  color: inherit;
}

.row-menu-separator {
  height: 1px;
  margin: 0.375rem 0.25rem;
  background: var(--escality-gray-200);
}

.row-menu-enter-active,
.row-menu-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.row-menu-enter-from,
.row-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
